import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Gallery, GalleryWithFieldValues, GalleryFormData } from '../types/gallery';

const COLLECTION_NAME = 'galleries';

export const galleryService = {
  // Get all galleries for an organization
  async getGalleries(organizationId: string): Promise<Gallery[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Gallery));
    } catch (error) {
      console.error('Error fetching galleries:', error);
      throw error;
    }
  },

  // Get published galleries (for frontend)
  async getPublishedGalleries(organizationId: string): Promise<Gallery[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        where('isPublished', '==', true),
        orderBy('publishedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Gallery));
    } catch (error) {
      console.error('Error fetching published galleries:', error);
      throw error;
    }
  },

  // Get single gallery by ID
  async getGalleryById(galleryId: string): Promise<Gallery | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, galleryId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Gallery;
      }
      return null;
    } catch (error) {
      console.error('Error fetching gallery:', error);
      throw error;
    }
  },

  // Get gallery by slug (for frontend)
  async getGalleryBySlug(slug: string, organizationId: string): Promise<Gallery | null> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        where('slug', '==', slug),
        where('isPublished', '==', true)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        } as Gallery;
      }
      return null;
    } catch (error) {
      console.error('Error fetching gallery by slug:', error);
      throw error;
    }
  },

  // Create new gallery
  async createGallery(
    data: GalleryFormData,
    userId: string,
    organizationId: string
  ): Promise<string> {
    try {
      const galleryData: GalleryWithFieldValues = {
        title: data.title,
        description: data.description || '',
        slug: data.slug,
        images: data.images?.map((img, index) => ({
          id: `img_${Date.now()}_${index}`,
          url: img.url,
          thumbnailUrl: img.thumbnailUrl,
          title: img.title,
          description: img.description,
          order: img.order,
          uploadedAt: Timestamp.now(),
        })) || [],
        coverImageUrl: data.coverImageUrl,
        isPublished: data.isPublished,
        publishedAt: data.isPublished ? Timestamp.now() : undefined,
        vehicleIds: data.vehicleIds || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        organizationId,
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), galleryData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating gallery:', error);
      throw error;
    }
  },

  // Update gallery
  async updateGallery(
    galleryId: string,
    data: Partial<GalleryFormData>
  ): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, galleryId);
      const updateData: any = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      // If publishing for the first time, set publishedAt
      if (data.isPublished) {
        const currentGallery = await this.getGalleryById(galleryId);
        if (currentGallery && !currentGallery.publishedAt) {
          updateData.publishedAt = Timestamp.now();
        }
      }

      // Handle images update
      if (data.images) {
        updateData.images = data.images.map((img, index) => ({
          id: img.id || `img_${Date.now()}_${index}`,
          url: img.url,
          thumbnailUrl: img.thumbnailUrl,
          title: img.title,
          description: img.description,
          order: img.order,
          uploadedAt: img.uploadedAt || Timestamp.now(),
        }));
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating gallery:', error);
      throw error;
    }
  },

  // Delete gallery
  async deleteGallery(galleryId: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, galleryId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting gallery:', error);
      throw error;
    }
  },

  // Get galleries for a specific vehicle
  async getGalleriesForVehicle(vehicleId: string, organizationId: string): Promise<Gallery[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        where('isPublished', '==', true),
        where('vehicleIds', 'array-contains', vehicleId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Gallery));
    } catch (error) {
      console.error('Error fetching galleries for vehicle:', error);
      throw error;
    }
  },

  // Generate slug from title
  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .trim()
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-'); // Remove multiple hyphens
  },
};
